import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { BottomNav } from "@/components/BottomNav";
import { MbCard } from "@/components/MbCard";
import { MbBadgeSun } from "@/components/MbBadgeSun";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { User as UserIcon, LogOut, Edit, Check, Languages } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { AppFooter } from "@/components/AppFooter";

interface Profile {
  id: string;
  name: string;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
}

interface UserRole {
  role: string;
}

interface Subscription {
  status: string;
  end_at: string;
  plan_code: string;
  source: string;
}

export default function Profile() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logo, strings, language, setLanguage } = useLanguage();

  useEffect(() => {
    // Set up auth listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session) {
          navigate("/auth");
        }
      }
    );

    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        fetchProfile(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (currentUser: User) => {
    
    try {

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("auth_user_id", currentUser.id)
        .single();

      if (profileError) throw profileError;

      setProfile(profileData);
      setName(profileData.name || "");
      setPhone(profileData.phone || "");
      setWhatsapp(profileData.whatsapp || "");
      setEmail(profileData.email || "");

      // Fetch user role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", currentUser.id)
        .limit(1)
        .single();

      setUserRole(roleData?.role || null);

      // Fetch subscription
      if (roleData?.role === "subscriber") {
        const { data: subData } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", profileData.id)
          .eq("status", "active")
          .order("end_at", { ascending: false })
          .limit(1)
          .single();

        setSubscription(subData);
      }
    } catch (error: any) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!profile) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          name: name.trim(),
          phone: phone.trim() || null,
          whatsapp: whatsapp.trim() || null,
          email: email.trim() || null,
        })
        .eq("id", profile.id);

      if (error) throw error;

      setEditing(false);
      if (user) fetchProfile(user);

      toast({
        title: "પ્રોફાઇલ અપડેટ થઈ",
        description: "તમારી માહિતી સાચવી લીધી છે",
      });
    } catch (error: any) {
      toast({
        title: "ભૂલ થઈ",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="animate-pulse text-muted-foreground">{strings.loading}</div>
      </div>
    );
  }

  const isActive = subscription?.status === "active";
  const expiryDate = subscription?.end_at 
    ? new Date(subscription.end_at).toLocaleDateString("gu-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Smart Farmer" className="h-10" />
            <h1 className="text-xl font-bold text-primary">{strings.profile}</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-6">
        {/* Language Switcher */}
        <MbCard elevated className="p-6">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Languages className="h-5 w-5" />
            {strings.languagePreference}
          </h3>
          <div className="flex gap-3">
            <Button
              variant={language === 'gu' ? 'default' : 'outline'}
              onClick={() => setLanguage('gu')}
              className="flex-1"
            >
              ગુજરાતી
            </Button>
            <Button
              variant={language === 'hi' ? 'default' : 'outline'}
              onClick={() => setLanguage('hi')}
              className="flex-1"
            >
              हिंदी
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {strings.languageHelper}
          </p>
        </MbCard>
        {/* Profile Info */}
        <MbCard elevated className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{profile?.name}</h2>
                {userRole === "admin" && (
                  <MbBadgeSun className="mt-1">એડમિન</MbBadgeSun>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => editing ? handleUpdate() : setEditing(true)}
            >
              {editing ? <Check className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">નામ</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!editing}
              />
            </div>
            <div>
              <Label htmlFor="phone">ફોન નંબર</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={!editing}
                placeholder="9876543210"
              />
            </div>
            <div>
              <Label htmlFor="whatsapp">WhatsApp નંબર</Label>
              <Input
                id="whatsapp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                disabled={!editing}
                placeholder="9876543210"
              />
            </div>
            <div>
              <Label htmlFor="email">ઇમેઇલ (વૈકલ્પિક)</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!editing}
                placeholder="your@email.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                પેમેન્ટ રસીદ માટે ઇમેઇલ ઉમેરો
              </p>
            </div>
          </div>
        </MbCard>

        {/* Subscription Status */}
        {userRole === "subscriber" && (
          <MbCard elevated className="p-6">
            <h3 className="text-lg font-bold mb-4">સબ્સ્ક્રિપ્શન સ્થિતિ</h3>
            
            {subscription ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">સ્થિતિ:</span>
                  <span
                    className={`font-semibold ${
                      isActive ? "text-primary" : "text-destructive"
                    }`}
                  >
                    {isActive ? strings.subscriptionActive : strings.subscriptionExpired}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">પ્લાન:</span>
                  <span className="font-semibold">{subscription.plan_code}</span>
                </div>
                {expiryDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {isActive ? strings.renewsOn : strings.expiresOn}
                    </span>
                    <span className="font-semibold">{expiryDate}</span>
                  </div>
                )}
                {subscription.source === "free" && (
                  <div className="bg-accent/20 p-3 rounded-lg text-center">
                    <p className="text-sm text-accent-foreground">
                      વિશેષ મફત સબ્સ્ક્રિપ્શન
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-4">
                  તમારી પાસે સક્રિય સબ્સ્ક્રિપ્શન નથી
                </p>
                <Button
                  variant="hero"
                  onClick={() => navigate("/subscribe")}
                >
                  {strings.subscribe}
                </Button>
              </div>
            )}
          </MbCard>
        )}

        {/* Admin Link */}
        {userRole === "admin" && (
          <Button
            variant="admin"
            className="w-full"
            onClick={() => navigate("/admin")}
          >
            એડમિન પેનલ
          </Button>
        )}

        {/* Logout Button */}
        <Button
          variant="destructive"
          className="w-full"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 w-4 h-4" />
          લૉગઆઉટ કરો
        </Button>
      </div>

      <div className="pb-16">
        <AppFooter />
      </div>

      <BottomNav />
    </div>
  );
}
